<template>
    <TinyEditor
        v-model="mutableValue"
        class="inserveEditor"
        :init="config"
        @drop="dropHandler($event);"
        @dragover="dragOverHandler($event);"
    />
</template>

<script>
import api from '@/services/Api';
import store from '@/store';
import TinyEditor from '@tinymce/tinymce-vue';
import tinymce from 'tinymce';

export default {
    components: { TinyEditor },
    props: {
        init: Object,
        modelValue: String,
        uploadToServer: {
            type: Boolean,
            default: true,
        },
        height: {
            type: Number,
            default: 310,
        },
        messageId: Number,
        publicAttachment: {
            type: Boolean,
            default: false
        },
        allowAttachmentsOtherThanImages: {
            type: Boolean,
            default: false,
        }
    },
    created () {
        if (this.init) {
            this.config = this.init;
        }
        this.mutableValue = this.modelValue;
    },
    data () {
        return {
            mutableValue: undefined,
            tinyKey: 1,
            config: {
                selector: '.inserveEditor',
                skin: 'oxide',
                content_css: 'default',
                setup: (editor) => {
                    // Only allow mentioning operators on tickets
                    if (this.$router.currentRoute.value.name?.split('.')[0] !== 'tickets') {
                        return;
                    }

                    const onAction = function (autocompleteApi, rng, value) {
                        editor.selection.setRng(rng);
                        editor.insertContent(value);
                        autocompleteApi.hide();
                    };

                    const specialChars = store.getters.getSortedList('operators').map((operator) => {
                        return { text: operator.full_name, value: `<span class="mceNonEditable" style="font-weight: 500; color: #20a8d8;" data-mention-operator-id="${operator.id}">@${operator.full_name}</span> ` };
                    });

                    const getMatchedChars = function (pattern) {
                        pattern = pattern.toLowerCase();
                        return specialChars.filter((char) => {
                            return char.text.toLowerCase().indexOf(pattern) !== -1;
                        });
                    };

                    /* An autocompleter that allows you to insert special characters */
                    editor.ui.registry.addAutocompleter('specialchars', {
                        ch: '@',
                        minChars: 1,
                        columns: 1,
                        onAction,
                        fetch (pattern) {
                            return new tinymce.util.Promise((resolve) => {
                                const results = getMatchedChars(pattern).map((char) => {
                                    return {
                                        type: 'cardmenuitem',
                                        value: char.value,
                                        text: char.text,
                                    };
                                });
                                resolve(results);
                            });
                        },
                    });
                },
                plugins: 'code link paste image lists autolink noneditable',
                toolbar: `undo redo | bold italic underline | alignleft aligncenter alignright | bullist numlist | link ${this.uploadToServer ? 'image ' : ''} forecolor | removeformat code pastetext`,
                height: this.height,
                browser_spellcheck: true,
                contextmenu: false,
                block_unsupported_drop: !this.allowAttachmentsOtherThanImages,
                images_file_types: 'jpeg,jpg,jpe,jfi,jif,jfif,png,gif,bmp',
                images_upload_handler: (blob, success, failure) => {
                    // We want to keep the base64 in some cases, so abort upload
                    if (!this.uploadToServer) return;

                    this.$emit('uploading');

                    const formData = new FormData();
                    formData.append('is_embedded', true);
                    formData.append('file', blob.blob(), blob.filename());

                    if (this.messageId) {
                        formData.append('message_id', this.messageId.toString());
                    }

                    if (this.publicAttachment) {
                        formData.append('is_public', true);
                    }

                    api.post('/attachments', formData, {
                        headers: {
                            'Content-Type': 'multipart/form-data'
                        }
                    }).then((response) => {
                        success(response.data.url);
                        this.$emit('uploaded');
                    }).catch((response) => {
                        this.$emit('uploaded');
                        failure();
                    });
                },
                menubar: false,
                language: this.$i18n.locale,
                paste_data_images: true,
                extended_valid_elements: 'contact-component[*],contact-telephone-component[*]',
                custom_elements: 'contact-component,contact-telephone-component',
                relative_urls: false,
                content_style: 'p { margin: 0; }',
                convert_urls: false,
            }
        };
    },
    watch: {
        modelValue (val) {
            this.mutableValue = val;
        },
        mutableValue () {
            this.$emit('update:modelValue', this.mutableValue);
            this.$emit('input', this.mutableValue);
        },
    },
    methods: {
        dropHandler (ev) {
            if (!this.allowAttachmentsOtherThanImages) {
                return;
            }

            const supportedTinyMCETypes = this.config.images_file_types.split(',').map(type => 'image/' + type);

            ev.preventDefault();

            let attachments = [];

            if (ev.dataTransfer.items) {
                // Use DataTransferItemList interface to access the file(s)
                [...ev.dataTransfer.items].forEach((item, i) => {
                    // If dropped items aren't images, add them as attachments instead.
                    if (!supportedTinyMCETypes.includes(item.type)) {
                        attachments.push(item.getAsFile());
                    }
                });
            } else {
                // Use DataTransfer interface to access the file(s)
                [...ev.dataTransfer.files].forEach((file, i) => {
                    // If dropped items aren't images, add them as attachments instead.
                    if (!supportedTinyMCETypes.includes(file.type)) {
                        attachments.push(file);
                    }
                });
            }

            this.$emit('upload-attachments', attachments)
        },
        dragOverHandler (ev) {
            if (!this.allowAttachmentsOtherThanImages) {
                return;
            }

            // Prevent default behavior (Prevent file from being opened)
            ev.preventDefault();
        }
    }
};
</script>
